// Discovery & OSINT API
import { api } from './client';

// Discovery scan interfaces
export interface CreateDiscoveryScanRequest {
  name: string;
  description?: string;
  target_ranges: string[];
  ping_methods?: string[];
  tcp_ping_ports?: string;
  udp_ping_ports?: string;
  resolve_dns?: boolean;
  detect_os?: boolean;
  check_top_ports?: boolean;
  top_port_count?: number;
  scan_profile?: string;
  threads?: number;
  timeout?: number;
  agent_id?: string;
  ping_retries?: number;
  include_web_ports?: boolean;
}

export interface QuickDiscoveryRequest {
  target_ranges: string[];
  agent_id?: string;
  scan_profile?: 'quick' | 'balanced' | 'thorough';
  ping_retries?: number;
  include_web_ports?: boolean;
}

export interface HostSelectionRequest {
  host_ids?: number[];
  is_selected?: boolean;
  select_all?: boolean;
  deselect_all?: boolean;
  exclude_suspicious?: boolean;
  exclude_imported?: boolean;
}

export interface ImportHostsRequest {
  use_hostname?: boolean;
  skip_duplicates?: boolean;
  add_prefix?: string;
  include_notes?: boolean;
}

export interface CreateDiscoveryScheduleRequest {
  name: string;
  description?: string;
  cron_expr: string;
  target_ranges: string[];
  ping_methods?: string[];
  tcp_ping_ports?: string;
  udp_ping_ports?: string;
  resolve_dns?: boolean;
  detect_os?: boolean;
  check_top_ports?: boolean;
  top_port_count?: number;
  scan_profile?: string;
  threads?: number;
  timeout?: number;
  agent_id?: string;
  notify_on_completion?: boolean;
  notify_on_failure?: boolean;
  notify_on_new_hosts?: boolean;
  notify_on_lost_hosts?: boolean;
  notify_on_changes?: boolean;
  notification_emails?: string;
  auto_generate_report?: boolean;
  report_format?: 'html' | 'pdf' | 'json';
  include_topology?: boolean;
  include_delta?: boolean;
  email_report_to?: string;
}

export interface DiscoverySchedule {
  id: number;
  project_id: number;
  created_by: number;
  name: string;
  description: string;
  enabled: boolean;
  cron_expr: string;
  next_run: string | null;
  last_run: string | null;
  target_ranges: string;
  ping_methods: string;
  tcp_ping_ports: string;
  udp_ping_ports: string;
  resolve_dns: boolean;
  detect_os: boolean;
  check_top_ports: boolean;
  top_port_count: number;
  scan_profile: string;
  threads: number;
  timeout: number;
  agent_id: string;
  run_count: number;
  max_runs: number;
  notify_on_completion: boolean;
  notify_on_failure: boolean;
  notify_on_new_hosts: boolean;
  notify_on_lost_hosts: boolean;
  notify_on_changes: boolean;
  notification_emails: string;
  auto_generate_report: boolean;
  report_format: string;
  include_topology: boolean;
  include_delta: boolean;
  email_report_to: string;
  created_at: string;
  updated_at: string;
}

export interface DiscoveryScheduleExecution {
  id: number;
  schedule_id: number;
  discovery_id: number;
  execution_number: number;
  scheduled_time: string;
  started_at: string | null;
  completed_at: string | null;
  duration: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  error_message: string;
  triggered_by: 'scheduler' | 'manual' | 'api';
  total_ips_scanned: number;
  alive_hosts_found: number;
  suspicious_hosts: number;
  new_hosts_count: number;
  lost_hosts_count: number;
  changed_hosts_count: number;
  notification_sent: boolean;
  report_generated: boolean;
  created_at: string;
}

export interface DiscoveryScan {
  id: number;
  project_id: number;
  name: string;
  description: string;
  target_ranges: string;
  ping_methods: string;
  tcp_ping_ports: string;
  udp_ping_ports: string;
  resolve_dns: boolean;
  detect_os: boolean;
  check_top_ports: boolean;
  top_port_count: number;
  scan_profile: string;
  threads: number;
  timeout: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  planned_ips: number;
  total_ips_scanned: number;
  alive_hosts_found: number;
  suspicious_hosts: number;
  agent_connected?: boolean;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  agent_id: string;
  is_agent_scan: boolean;
  command_id: string;
}

export interface DiscoveredHost {
  id: number;
  discovery_scan_id: number;
  ip_address: string;
  hostname: string;
  mac_address: string;
  mac_vendor: string;
  is_alive: boolean;
  alive_reason: string;
  response_time_ms: number;
  ttl: number;
  open_ports: string;
  open_port_count: number;
  os_guess: string;
  os_accuracy: number;
  device_type: string;
  is_suspicious: boolean;
  suspicious_reason: string;
  suspicious_score: number;
  netbios_name: string;
  dns_names: string;
  service_banners?: string;
  is_selected: boolean;
  imported_as_target: boolean;
  target_id: number;
  created_at: string;
  mac_group_count?: number;
}

export interface DiscoveryHostsResponse {
  hosts: DiscoveredHost[];
  stats: {
    total: number;
    alive: number;
    suspicious: number;
    selected: number;
    mac_groups: number;
  };
  mac_groups: Record<string, number>;
}

export interface FalsePositivePattern {
  id: number;
  name: string;
  description: string;
  pattern_type: string;
  pattern_value: string;
  is_active: boolean;
  is_built_in: boolean;
  created_at: string;
}

export interface DiscoveryAgent {
  agent_id: string;
  name: string;
  hostname: string;
  status: string;
  is_connected: boolean;
}

export const discoveryAPI = {
  getScans: (projectId: number) => {
    console.log('🔍 Fetching discovery scans for project:', projectId);
    return api.get(`/projects/${projectId}/discovery`);
  },
  createScan: (projectId: number, data: CreateDiscoveryScanRequest) => {
    console.log('➕ Creating discovery scan for project:', projectId);
    return api.post(`/projects/${projectId}/discovery`, data);
  },
  quickScan: (projectId: number, data: QuickDiscoveryRequest) => {
    console.log('⚡ Starting quick discovery scan for project:', projectId);
    return api.post(`/projects/${projectId}/quick-discovery`, data);
  },
  getScan: (scanId: number) => {
    console.log('🔍 Fetching discovery scan:', scanId);
    return api.get(`/discovery/${scanId}`);
  },
  exportHostsCsv: (scanId: number) => {
    return api.get(`/discovery/${scanId}/export/csv`, { responseType: 'blob' });
  },
  startScan: (scanId: number) => {
    console.log('▶️ Starting discovery scan:', scanId);
    return api.post(`/discovery/${scanId}/start`);
  },
  stopScan: (scanId: number) => {
    console.log('⏹️ Stopping discovery scan:', scanId);
    return api.post(`/discovery/${scanId}/stop`);
  },
  deleteScan: (scanId: number) => {
    console.log('🗑️ Deleting discovery scan:', scanId);
    return api.delete(`/discovery/${scanId}`);
  },
  getHosts: (scanId: number, params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('🖥️ Fetching discovered hosts for scan:', scanId);
    return api.get<DiscoveryHostsResponse>(`/discovery/${scanId}/hosts${queryString}`);
  },
  updateHostSelection: (scanId: number, data: HostSelectionRequest) => {
    console.log('✅ Updating host selection for scan:', scanId);
    return api.put(`/discovery/${scanId}/hosts/selection`, data);
  },
  importHosts: (scanId: number, data: ImportHostsRequest) => {
    console.log('📥 Importing hosts as targets from scan:', scanId);
    return api.post(`/discovery/${scanId}/import`, data);
  },
  getPatterns: () => {
    console.log('📋 Fetching false positive patterns');
    return api.get<{ patterns: FalsePositivePattern[]; total: number }>('/discovery/patterns');
  },
  createPattern: (data: Partial<FalsePositivePattern>) => {
    console.log('➕ Creating false positive pattern:', data.name);
    return api.post('/discovery/patterns', data);
  },
  updatePattern: (patternId: number, data: Partial<FalsePositivePattern>) => {
    console.log('📝 Updating false positive pattern:', patternId);
    return api.put(`/discovery/patterns/${patternId}`, data);
  },
  getAgents: () => {
    console.log('🤖 Fetching available agents for discovery');
    return api.get<{ agents: DiscoveryAgent[]; total: number }>('/discovery/agents');
  },
  getSchedules: (projectId: number) => {
    console.log('📅 Fetching discovery schedules for project:', projectId);
    return api.get(`/projects/${projectId}/discovery-schedules`);
  },
  createSchedule: (projectId: number, data: CreateDiscoveryScheduleRequest) => {
    console.log('➕ Creating discovery schedule for project:', projectId);
    return api.post(`/projects/${projectId}/discovery-schedules`, data);
  },
  getSchedule: (scheduleId: number) => {
    console.log('🔍 Fetching discovery schedule:', scheduleId);
    return api.get(`/discovery-schedules/${scheduleId}`);
  },
  updateSchedule: (scheduleId: number, data: Partial<CreateDiscoveryScheduleRequest>) => {
    console.log('📝 Updating discovery schedule:', scheduleId);
    return api.put(`/discovery-schedules/${scheduleId}`, data);
  },
  deleteSchedule: (scheduleId: number) => {
    console.log('🗑️ Deleting discovery schedule:', scheduleId);
    return api.delete(`/discovery-schedules/${scheduleId}`);
  },
  runScheduleNow: (scheduleId: number) => {
    console.log('▶️ Running discovery schedule now:', scheduleId);
    return api.post(`/discovery-schedules/${scheduleId}/run`);
  },
  getScheduleExecutions: (scheduleId: number) => {
    console.log('📋 Fetching schedule executions:', scheduleId);
    return api.get(`/discovery-schedules/${scheduleId}/executions`);
  },
  exportHTML: (scanId: number, language?: string) => {
    console.log('📄 Exporting HTML report for discovery scan:', scanId);
    const params = new URLSearchParams();
    if (language) params.append('language', language);
    const queryString = params.toString();
    return api.get(`/discovery/${scanId}/export/html${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob',
      timeout: 60000,
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
  },
};

// OSINT Intelligence System API
export const osintAPI = {
  getAPIKeys: () => {
    console.log('🔑 Fetching OSINT API keys');
    return api.get('/osint/api-keys');
  },
  getAPIKey: (service: string) => {
    console.log('🔑 Fetching OSINT API key for service:', service);
    return api.get(`/osint/api-keys/${service}`);
  },
  createOrUpdateAPIKey: (data: any) => {
    console.log('💾 Creating/updating OSINT API key:', data.service);
    return api.post('/osint/api-keys', data);
  },
  deleteAPIKey: (service: string) => {
    console.log('🗑️ Deleting OSINT API key for service:', service);
    return api.delete(`/osint/api-keys/${service}`);
  },
  validateAPIKey: (service: string) => {
    console.log('✅ Validating OSINT API key for service:', service);
    return api.post(`/osint/api-keys/${service}/validate`);
  },
  getServiceTemplates: () => {
    console.log('📋 Fetching OSINT service templates');
    return api.get('/osint/service-templates');
  },
  getServiceTemplate: (service: string) => {
    console.log('📋 Fetching OSINT service template:', service);
    return api.get(`/osint/service-templates/${service}`);
  },
  getOSINTConfig: () => {
    console.log('⚙️ Fetching OSINT configuration');
    return api.get('/osint/config');
  },
  getUsageStats: () => {
    console.log('📊 Fetching OSINT usage statistics');
    return api.get('/osint/usage-stats');
  },
  getGlobalUsageStats: () => {
    console.log('🌍 Fetching OSINT global usage statistics');
    return api.get('/admin/osint/global-usage-stats');
  },
  performLookup: (service: string, query: string, options?: any) => {
    console.log('🔍 Performing OSINT lookup:', service, query);
    return api.post(`/osint/lookup/${service}`, { query, ...options });
  },
  getLookupHistory: (params?: URLSearchParams) => {
    const queryString = params ? `?${params.toString()}` : '';
    console.log('📜 Fetching OSINT lookup history', queryString);
    return api.get(`/osint/lookup-history${queryString}`);
  },
  getLookupResult: (id: number) => {
    console.log('📊 Fetching OSINT lookup result:', id);
    return api.get(`/osint/lookup-results/${id}`);
  },
};
